package br.com.vvrefeicoes.portal.feature.shell

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.material3.Badge
import androidx.compose.material3.BadgedBox
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppLogoWhite
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.SearchableSelect
import br.com.vvrefeicoes.portal.core.ui.layout.appHeaderHorizontalPadding
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.feature.notifications.LocalNotifications
import br.com.vvrefeicoes.portal.feature.notifications.htmlToPlain
import com.composables.icons.lucide.ArrowRight
import com.composables.icons.lucide.Bell
import com.composables.icons.lucide.Building
import com.composables.icons.lucide.ChevronDown
import com.composables.icons.lucide.CircleUser
import com.composables.icons.lucide.LogOut
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.PanelLeftOpen
import com.composables.icons.lucide.SlidersHorizontal
import com.composables.icons.lucide.User
import com.composables.icons.lucide.UserRound
import com.composables.icons.lucide.UsersRound
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.notifications_empty
import portalvv.shared.generated.resources.notifications_go_to
import portalvv.shared.generated.resources.shell_filters

private val TopBarHeight = 64.dp
private val SelectorWidth = 220.dp
private val SelectorGap = AppSpacing.space12
private val TopBarControlsReserve = 320.dp
private val TopBarLeadingReserve = 200.dp

internal fun topBarNeedsCompact(
    maxWidth: Dp,
    visibleSelectors: Int,
    reserved: Dp = TopBarControlsReserve,
): Boolean {
    if (visibleSelectors == 0) return false
    val selectors = SelectorWidth * visibleSelectors + SelectorGap * (visibleSelectors - 1)
    return maxWidth < selectors + reserved
}

@Composable
fun AdminTopBar(
    sidebarExpanded: Boolean,
    onExpandSidebar: () -> Unit,
    onHome: () -> Unit,
    filters: GlobalFilters,
    units: List<String>,
    managers: List<String>,
    directors: List<String>,
    showUnits: Boolean,
    showManagers: Boolean,
    showDirectors: Boolean,
    userName: String,
    onGoToNotifications: () -> Unit,
    onGoToProfile: () -> Unit,
    onLogout: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val visibleSelectors = listOf(showUnits, showDirectors, showManagers).count { it }
    var filtersExpanded by remember { mutableStateOf(false) }

    Surface(
        modifier = modifier,
        color = MaterialTheme.colorScheme.primary,
        contentColor = MaterialTheme.colorScheme.onPrimary,
    ) {
        BoxWithConstraints {
            val leadingReserve = if (sidebarExpanded) 0.dp else TopBarLeadingReserve
            val compact = topBarNeedsCompact(maxWidth, visibleSelectors, TopBarControlsReserve + leadingReserve)
            val horizontalPadding = appHeaderHorizontalPadding(maxWidth)

            Row(
                modifier =
                    Modifier
                        .fillMaxWidth()
                        .height(TopBarHeight)
                        .padding(horizontal = horizontalPadding),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
            ) {
                if (!sidebarExpanded) {
                    IconButton(onClick = onExpandSidebar) {
                        Icon(Lucide.PanelLeftOpen, contentDescription = "Expandir menu")
                    }
                    AppLogoWhite(
                        modifier =
                            Modifier
                                .height(28.dp)
                                .clickable { onHome() },
                        contentDescription = "Ir para o início",
                    )
                }

                Box(
                    modifier = Modifier.weight(1f),
                    contentAlignment = Alignment.Center,
                ) {
                    if (!compact) {
                        Row(horizontalArrangement = Arrangement.spacedBy(SelectorGap)) {
                            if (showDirectors) {
                                FilterSelect(
                                    leadingIcon = Lucide.UserRound,
                                    options = directors,
                                    selected = filters.director,
                                    onSelect = filters::selectDirector,
                                    modifier = Modifier.width(SelectorWidth),
                                    onPrimary = true,
                                )
                            }
                            if (showManagers) {
                                FilterSelect(
                                    leadingIcon = Lucide.UsersRound,
                                    options = managers,
                                    selected = filters.manager,
                                    onSelect = filters::selectManager,
                                    modifier = Modifier.width(SelectorWidth),
                                    onPrimary = true,
                                )
                            }
                            if (showUnits) {
                                FilterSelect(
                                    leadingIcon = Lucide.Building,
                                    options = units,
                                    selected = filters.unit,
                                    onSelect = filters::selectUnit,
                                    modifier = Modifier.width(SelectorWidth),
                                    onPrimary = true,
                                )
                            }
                        }
                    }
                }

                if (compact && visibleSelectors > 0) {
                    IconButton(onClick = { filtersExpanded = true }) {
                        Icon(
                            imageVector = Lucide.SlidersHorizontal,
                            contentDescription = stringResource(Res.string.shell_filters),
                        )
                    }
                }

                NotificationsMenu(onGoToNotifications = onGoToNotifications)
                UserMenu(
                    userName = userName,
                    onGoToProfile = onGoToProfile,
                    onLogout = onLogout,
                )
            }
        }
    }

    if (filtersExpanded && visibleSelectors > 0) {
        AppModalCard(
            title = stringResource(Res.string.shell_filters),
            onDismiss = { filtersExpanded = false },
        ) {
            if (showDirectors) {
                FilterSelect(
                    leadingIcon = Lucide.UserRound,
                    options = directors,
                    selected = filters.director,
                    onSelect = filters::selectDirector,
                    modifier = Modifier.fillMaxWidth(),
                    onPrimary = false,
                    underlined = true,
                )
            }
            if (showManagers) {
                FilterSelect(
                    leadingIcon = Lucide.UsersRound,
                    options = managers,
                    selected = filters.manager,
                    onSelect = filters::selectManager,
                    modifier = Modifier.fillMaxWidth(),
                    onPrimary = false,
                    underlined = true,
                )
            }
            if (showUnits) {
                FilterSelect(
                    leadingIcon = Lucide.Building,
                    options = units,
                    selected = filters.unit,
                    onSelect = filters::selectUnit,
                    modifier = Modifier.fillMaxWidth(),
                    onPrimary = false,
                    underlined = true,
                )
            }
        }
    }
}

@Composable
private fun FilterSelect(
    leadingIcon: ImageVector,
    options: List<String>,
    selected: String,
    onSelect: (String) -> Unit,
    modifier: Modifier,
    onPrimary: Boolean,
    underlined: Boolean = false,
) {
    SearchableSelect(
        leadingIcon = leadingIcon,
        options = options,
        selected = selected,
        onSelect = onSelect,
        modifier = modifier,
        containerColor = if (onPrimary) MaterialTheme.colorScheme.onPrimary.copy(alpha = 0.12f) else MaterialTheme.colorScheme.surface,
        contentColor = if (onPrimary) MaterialTheme.colorScheme.onPrimary else MaterialTheme.colorScheme.onSurface,
        iconColor = if (onPrimary) MaterialTheme.colorScheme.onPrimary else MaterialTheme.colorScheme.onSurfaceVariant,
        underlined = underlined,
        searchable = true,
        showTriggerBorder = false,
    )
}

@Composable
private fun NotificationsMenu(onGoToNotifications: () -> Unit) {
    var expanded by remember { mutableStateOf(false) }
    val notifications = LocalNotifications.current

    Box {
        IconButton(onClick = { expanded = true }) {
            BadgedBox(
                badge = {
                    if (notifications.unreadCount > 0) {
                        Badge { Text(notifications.unreadCount.toString()) }
                    }
                },
            ) {
                Icon(Lucide.Bell, contentDescription = "Notificações")
            }
        }
        DropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            modifier = Modifier.width(360.dp),
        ) {
            Text(
                text = "Notificações",
                style = MaterialTheme.typography.titleSmall,
                modifier = Modifier.padding(horizontal = AppSpacing.space16, vertical = AppSpacing.space8),
            )
            HorizontalDivider()
            val recent = notifications.items.take(5)
            if (recent.isEmpty()) {
                Text(
                    text = stringResource(Res.string.notifications_empty),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.padding(horizontal = AppSpacing.space16, vertical = AppSpacing.space8),
                )
            } else {
                recent.forEach { notification ->
                    DropdownMenuItem(
                        text = {
                            Column {
                                Text(
                                    text = notification.title,
                                    style = MaterialTheme.typography.bodyMedium,
                                    fontWeight = if (notification.read) FontWeight.Normal else FontWeight.SemiBold,
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis,
                                )
                                Text(
                                    text = htmlToPlain(notification.body),
                                    style = MaterialTheme.typography.labelSmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis,
                                )
                            }
                        },
                        onClick = {
                            expanded = false
                            onGoToNotifications()
                        },
                    )
                }
            }
            HorizontalDivider()
            DropdownMenuItem(
                text = { Text(stringResource(Res.string.notifications_go_to)) },
                trailingIcon = { Icon(Lucide.ArrowRight, contentDescription = null) },
                onClick = {
                    expanded = false
                    onGoToNotifications()
                },
            )
        }
    }
}

@Composable
private fun UserMenu(
    userName: String,
    onGoToProfile: () -> Unit,
    onLogout: () -> Unit,
) {
    var expanded by remember { mutableStateOf(false) }

    Box {
        Surface(
            onClick = { expanded = true },
            color = Color.Transparent,
            contentColor = MaterialTheme.colorScheme.onPrimary,
            shape = AppShapes.medium,
        ) {
            Row(
                modifier = Modifier.padding(horizontal = AppSpacing.space12, vertical = AppSpacing.space8),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
            ) {
                Icon(Lucide.CircleUser, contentDescription = null)
                Text(text = userName, style = MaterialTheme.typography.bodyMedium)
                Icon(Lucide.ChevronDown, contentDescription = null)
            }
        }
        DropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
        ) {
            DropdownMenuItem(
                text = { Text("Perfil") },
                leadingIcon = { Icon(Lucide.User, contentDescription = null) },
                onClick = {
                    expanded = false
                    onGoToProfile()
                },
            )
            DropdownMenuItem(
                text = { Text("Sair") },
                leadingIcon = { Icon(Lucide.LogOut, contentDescription = null) },
                onClick = {
                    expanded = false
                    onLogout()
                },
            )
        }
    }
}
